/**
 * ContactSalesModal - shared enterprise lead-capture form.
 * Opened from the global "Contact Sales" CTA in PlatformFooter.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { X, Building2, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAM_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'];

export default function ContactSalesModal({ open, onClose }) {
  const [form, setForm] = useState({
    full_name: '', work_email: '', company: '', phone: '', team_size: '', use_case: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.full_name.trim().length >= 2 && /\S+@\S+\.\S+/.test(form.work_email) && form.company.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setError('Please fill in your name, a valid work email, and company.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/sales/lead`, {
        ...form,
        source_page: typeof window !== 'undefined' ? window.location.pathname : null,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const close = () => {
    setDone(false); setError('');
    setForm({ full_name: '', work_email: '', company: '', phone: '', team_size: '', use_case: '', message: '' });
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="contact-sales-modal" onClick={close}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative bg-[#131314] text-white px-6 py-5 rounded-t-2xl">
          <button onClick={close} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors" data-testid="contact-sales-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-coral-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-coral-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold leading-tight">Talk to Sales</h2>
              <p className="text-xs text-white/60">Enterprise notarization, escrow &amp; compliance, built for your team.</p>
            </div>
          </div>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="contact-sales-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-1">Message sent</h3>
            <p className="text-slate-600 text-sm mb-6">Thanks! Our team will reach out within 1 business day. Check your inbox for a confirmation.</p>
            <Button onClick={close} className="bg-coral-500 hover:bg-coral-600 text-white" data-testid="contact-sales-done-btn">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Full name *</label>
                <Input value={form.full_name} onChange={set('full_name')} placeholder="Jane Doe" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Work email *</label>
                <Input type="email" value={form.work_email} onChange={set('work_email')} placeholder="jane@company.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Company *</label>
                <Input value={form.company} onChange={set('company')} placeholder="Acme Title Co." data-testid="contact-company-input" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Phone</label>
                <Input value={form.phone} onChange={set('phone')} placeholder="(555) 123-4567" data-testid="contact-phone-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Team size</label>
                <select value={form.team_size} onChange={set('team_size')} className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-navy-900 text-sm h-9" data-testid="contact-teamsize-select">
                  <option value="">Select…</option>
                  {TEAM_SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Primary use case</label>
                <Input value={form.use_case} onChange={set('use_case')} placeholder="RON, escrow, title…" data-testid="contact-usecase-input" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">How can we help?</label>
              <textarea value={form.message} onChange={set('message')} rows={3} maxLength={2000}
                placeholder="Tell us about your volume, timeline, and goals." data-testid="contact-message-input"
                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-navy-900 text-sm" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="contact-sales-error">{error}</p>}
            <Button type="submit" disabled={busy || !valid} className="w-full bg-coral-500 hover:bg-coral-600 text-white" data-testid="contact-sales-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {busy ? 'Sending…' : 'Send to Sales'}
            </Button>
            <p className="text-[11px] text-slate-400 text-center">We'll only use your details to respond to your inquiry.</p>
          </form>
        )}
      </div>
    </div>
  );
}
